import { ApolloError } from "@apollo/client/core";
import { GraphService } from "../../../graphql/services/graph.service";
import { ListingItemMutatorInterface } from "../../interfaces/listing-item-mutator.interface";
import { catchError, map, Observable, of, throwError } from "rxjs";
import { MutationResult } from "apollo-angular";
import { GraphqlMessageInterface } from "../../../graphql/interfaces/graphql-message.interface";
import { ListItemRefModel } from "../models/list-item-ref.model";
import { ToastService } from "../../../tools/message/service/toast.service";

export abstract class ListItemMutator implements ListingItemMutatorInterface {
  protected constructor(
    protected graphService: GraphService,
    protected rowRef: ListItemRefModel,
    protected messageService?: ToastService
  ) {}

  /**
   * Sends a simple mutation request.
   * @param mutationName
   * @param id
   */
  protected sendSimpleActionRequest(
    mutationName: string,
    id: string,
    params?: any,
    paramsDef?: any,
    query?: string[]
  ): Observable<MutationResult<any>> {
    console.log("****");

    return this.graphService.constructMutation<GraphqlMessageInterface>(
      mutationName,
      paramsDef ?? { id: "ID" },
      params ?? { id },
      query ?? ["text", "code"]
    );
  }

  /**
   * Executes an action mutation, then commit the changes or revert it.
   * @param mutationName
   */
  protected executeAction(
    mutationName: string,
    params?: any,
    paramsDef?: any,
    query?: string[],
    onError?: (error: ApolloError) => void
  ): Observable<MutationResult<any>> {
    return this.sendSimpleActionRequest(mutationName, this.rowRef.get("id"), params, paramsDef, query).pipe(
      map((data: MutationResult<GraphqlMessageInterface>) => {
        // Handle Response.
        if (!data?.errors) {
          this.rowRef.commit();
        } else {
          this.rowRef.revert();
          throw data.errors;
        }
        return data;
      }),
      catchError((error: any) => {
        // Handle Error.
        const handleError = () => {
          const msg = error.graphQLErrors?.map((err: any) => err.message).join(", ");
          this.messageService?.error(msg);
        };

        onError ? onError(error) : handleError();
        this.rowRef.revert();
        return throwError(() => error);
      })
    );
  }

  /**
   * Execute remove table row item.
   * @param mutationName
   */
  remove(
    mutationName: string,
    params: any = undefined,
    paramsDef?: any,
    query?: string[],
    onError?: (error: ApolloError) => void
  ): Observable<MutationResult<any>> {
    this.rowRef.remove(false);
    return this.executeAction(mutationName, params, paramsDef, query, onError);
  }

  /**
   * Execute mutation then add a class to the item.
   * @param mutationName
   * @param _class
   */
  addClass(
    mutationName: string,
    _class: string,
    paramsDef?: any,
    params?: any,
    query = ["text", "code"],
    onError?: (error: ApolloError) => void
  ): Observable<MutationResult<any>> {
    this.rowRef.addClass(_class, false);
    return this.executeAction(mutationName, params, paramsDef, query, onError);
  }

  /**
   * Execute mutation then remove a class from the item.
   * @param mutationName
   * @param _class
   */
  removeClass(
    mutationName: string,
    _class: string,
    paramsDef?: any,
    params?: any,
    query = ["text", "code"],
    onError?: (error: ApolloError) => void
  ): Observable<MutationResult<any>> {
    this.rowRef.removeClass(_class, false);
    return this.executeAction(mutationName, params, paramsDef, query, onError);
  }
}
